import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import type { AwardReference, AwardReview } from '../../types/awards';
import { KIND_TITLE, RECLASSIFY_KINDS, STATUS_LABEL, levelLabel, minutesText, reviewSentence } from '../../utils/awardReviews';

/**
 * One award pay review reminder with what HR can do about it: change the
 * level (where the caller offers it), record that no change is needed (with a
 * reason, kept on the record), or dismiss it.
 */
export default function AwardReviewItem({
  review, reference, today, formatDate, onResolve, onReclassify, showPersonLink = false, busy = false,
}: {
  review: AwardReview;
  reference: AwardReference | null;
  today: string;
  formatDate: (d: string) => string;
  onResolve?: (outcome: 'no_change' | 'dismissed', note: string) => void;
  /** Offered on the staff profile, where the level picker is. */
  onReclassify?: () => void;
  showPersonLink?: boolean;
  busy?: boolean;
}) {
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState('');
  const open = review.status === 'open';
  const overdue = open && review.due_on < today;

  return (
    <li className={`rounded-lg border p-3 space-y-2 ${open ? (overdue ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40') : 'border-neutral-200 bg-white'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-neutral-900">{KIND_TITLE[review.kind]}</span>
        {showPersonLink && review.user_id && (
          <Link to={`/hr/staff?tab=award&user=${review.user_id}`} className="text-sm text-primary-700 hover:underline break-words min-w-0">
            {review.name}
          </Link>
        )}
        <span className={`text-xs px-2 py-0.5 rounded-full ${overdue ? 'bg-red-100 text-red-700' : open ? 'bg-amber-100 text-amber-800' : 'bg-neutral-100 text-neutral-600'}`}>
          {open ? (overdue ? `Was due ${formatDate(review.due_on)}` : `Due ${formatDate(review.due_on)}`) : STATUS_LABEL[review.status]}
        </span>
      </div>
      <p className="text-sm text-neutral-700 break-words">
        {reviewSentence(showPersonLink ? review : { ...review, name: null }, reference, formatDate, today)}
      </p>
      {review.kind === 'competency' && (review.detail.items?.length ?? 0) > 0 && (
        <ul className="text-xs text-neutral-600 space-y-0.5">
          {review.detail.items!.map((it, i) => (
            <li key={`${it.source}-${i}`} className="break-words">
              {formatDate(it.on)}: {it.label} ({levelLabel(reference, review.detail.award_code, it.level_key)})
            </li>
          ))}
        </ul>
      )}
      {review.kind === 'higher_duties' && (review.detail.days?.length ?? 0) > 0 && (
        <ul className="text-xs text-neutral-600 space-y-1">
          {review.detail.days!.map(day => (
            <li key={day.date} className="break-words">
              <span className="font-medium text-neutral-800">{formatDate(day.date)}:</span>{' '}
              {day.levels.map(l => `${levelLabel(reference, day.award_code, l.level_key)} duties ${minutesText(l.minutes)}`).join(', ')}
              {day.whole_day_level
                ? <span className="text-red-700">{` — ${levelLabel(reference, day.award_code, day.whole_day_level)} for the whole day`}</span>
                : ' — the higher level for that time'}
              {day.labels.length > 0 && <span className="text-neutral-500">{` (${day.labels.join(', ')})`}</span>}
            </li>
          ))}
        </ul>
      )}
      {!open && (
        <p className="text-xs text-neutral-500 break-words">
          {STATUS_LABEL[review.status]}
          {review.resolved_by_name ? ` by ${review.resolved_by_name}` : ''}
          {review.resolved_at ? ` on ${formatDate(review.resolved_at.slice(0, 10))}` : ''}
          {review.resolution_note ? `: ${review.resolution_note}` : ''}
        </p>
      )}
      {open && onResolve && (
        noting ? (
          <div className="space-y-2">
            <label className="block text-xs text-neutral-600" htmlFor={`award-note-${review.id}`}>
              Why is no change needed? This is kept with the record.
            </label>
            <textarea
              id={`award-note-${review.id}`}
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy || !note.trim()} onClick={() => onResolve('no_change', note)}>
                Record no change
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => { setNoting(false); setNote(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {onReclassify && review.user_id && RECLASSIFY_KINDS.includes(review.kind) && (
              <Button size="sm" disabled={busy} onClick={onReclassify}>Change level</Button>
            )}
            {showPersonLink && review.user_id && (
              <Link
                to={`/hr/staff?tab=award&user=${review.user_id}`}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50"
              >
                Open profile
              </Link>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setNoting(true)}>No change needed</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onResolve('dismissed', '')}>Dismiss</Button>
          </div>
        )
      )}
    </li>
  );
}
